import { useTransactionsDetail } from "Api/Queries/Transactions/useTransactionsDetail";
import { AuthenticatedLayout } from "Components/Layout/AuthenticatedLayout";
import { CenteredCircularProgress } from "Components/Shared/CenteredCircularProgress";
import { ErrorText } from "Components/Shared/Errors/ErrorText";
import { PrimaryPaper } from "Components/Shared/PrimaryPaper";
import { StyledPageWrapper } from "Components/Shared/StyledComponents";
import { TransactionDetail } from "Components/Transactions/TransactionDetail";
import { useLoggedInUser } from "Hooks/useLoggedInUser";
import { Resources, useResource } from "Translations/Resources";
import { TransactionDirection } from "Infrastructure/Api/Api";
import {
  getTransactionDirection,
  type ViewerTransactionDirection,
} from "Utils/TransactionUtils";
import { appUrl } from "Utils/UrlUtils";
import type { ReactNode } from "react";
import { useParams } from "react-router-dom";

export const TransactionDetailPage = () => {
  const { t } = useResource();
  const { id } = useParams();
  const user = useLoggedInUser();
  const { data, isLoading, isError } = useTransactionsDetail({ publicID: id });

  if (isLoading) {
    return (
      <Layout title="">
        <PrimaryPaper sx={{ height: "350px" }}>
          <CenteredCircularProgress />
        </PrimaryPaper>
      </Layout>
    );
  }

  if (!data || isError || !id) {
    return (
      <Layout title={t(Resources.Pages.Titles.UnknownTransaction)}>
        <PrimaryPaper>
          <ErrorText>
            {t(Resources.TransactionErrors.TransactionNotFound)}
          </ErrorText>
        </PrimaryPaper>
      </Layout>
    );
  }

  const directionToTitle: Record<ViewerTransactionDirection, string> = {
    [TransactionDirection.Incoming]: t(
      Resources.Pages.Titles.IncomingTransaction,
    ),
    [TransactionDirection.Outgoing]: t(
      Resources.Pages.Titles.OutgoingTransaction,
    ),
    [TransactionDirection.Unknown]: t(
      Resources.Pages.Titles.UnknownTransaction,
    ),
  };

  const currentUserID = user?.userID || -1;
  const direction = getTransactionDirection(data, currentUserID);

  return (
    <Layout title={directionToTitle[direction]}>
      <TransactionDetail publicID={id} />
    </Layout>
  );
};

export default TransactionDetailPage;

type LayoutProps = {
  children: ReactNode;
  title: string;
};

const Layout = ({ children, title }: LayoutProps) => (
  <AuthenticatedLayout
    title={title}
    backNavigation={{
      mode: "historyStack",
      fallbackUrl: appUrl("transactions"),
    }}
  >
    <StyledPageWrapper>
      <div className="left-content">{children}</div>
    </StyledPageWrapper>
  </AuthenticatedLayout>
);
